import React from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import "../css/privacy.scss";

function Privacy() {
  return (
    <div className="privacy legal-page">
      <Header />
      <main id="MainContent">
        <article id="cms-content" aria-labelledby="privacy-title">
          <div>
            <p className="legal-page__eyebrow">Legal &amp; privacy</p>
            <h1 id="privacy-title">Privacy Policy</h1>
            <p className="legal-page__updated">
              <strong>Last updated: October 16, 2026</strong>
            </p>
            <p className="legal-page__intro">
              This Privacy Policy explains how averlynxstore collects, uses,
              discloses, retains, and protects personal data when you visit our
              website, shop with us, contact customer care, or otherwise use our
              services.
            </p>

            <section className="legal-page__section">
              <h2>1. Scope and who is responsible</h2>
              <p>
                averlynxstore is responsible for the personal data described in
                this policy, except where a checkout, payment, delivery, social
                media, or other independent provider explains that it processes
                data under its own privacy notice.
              </p>
              <p>
                This policy applies to our online store and related customer
                services. It does not apply to an unrelated third-party website
                merely because we link to it.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>2. Personal data we collect</h2>
              <ul>
                <li>
                  <strong>Identity and contact data:</strong> name, email,
                  telephone number, delivery and billing addresses, and account
                  identifiers.
                </li>
                <li>
                  <strong>Order and transaction data:</strong> products viewed
                  or purchased, cart activity, order status, returns, discounts,
                  currency, and transaction references.
                </li>
                <li>
                  <strong>Payment-related data:</strong> payment choice,
                  authorization result, billing details, and limited card or
                  wallet information supplied by a payment provider. Full
                  payment credentials are generally handled by the provider
                  presented at checkout.
                </li>
                <li>
                  <strong>Device and usage data:</strong> IP address, browser,
                  device identifiers, approximate location, referring page,
                  pages and features used, and diagnostic or security events.
                </li>
                <li>
                  <strong>Preference and marketing data:</strong> language,
                  region, size or product interests, subscription status, and
                  communication interactions.
                </li>
                <li>
                  <strong>Support and content data:</strong> messages, survey
                  responses, reviews, photographs, product claims, and other
                  information you choose to provide.
                </li>
              </ul>
              <p>
                Please do not send sensitive personal data that we have not
                requested. If limited sensitive data is necessary to handle a
                legal, accessibility, safety, or fraud matter, we use it only
                for that purpose and as permitted by law.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>3. Sources of personal data</h2>
              <p>
                We receive data directly from you, automatically from your
                browser or device, from people who purchase or arrange delivery
                for you, and from providers involved in payments, fraud
                prevention, hosting, analytics, marketing, fulfillment,
                delivery, and customer support. We may also receive lawful
                public or commercial information used to protect transactions
                and the service.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>4. Why we use personal data</h2>
              <ul>
                <li>Operate the website, cart, checkout, and account features.</li>
                <li>Accept, verify, fulfill, deliver, return, and refund orders.</li>
                <li>Provide customer care and respond to privacy requests.</li>
                <li>Detect fraud, abuse, security incidents, and technical errors.</li>
                <li>Personalize content and remember choices.</li>
                <li>
                  Measure and improve products, services, accessibility, and
                  site performance.
                </li>
                <li>
                  Send marketing when we have the consent or other lawful basis
                  required for the channel and location.
                </li>
                <li>
                  Maintain business records, establish or defend legal claims,
                  and comply with tax, accounting, consumer, safety, sanctions,
                  and other legal duties.
                </li>
              </ul>
              <p>
                Where a legal basis is required, we rely as appropriate on
                performance of a contract, steps requested before a contract,
                compliance with law, our or another party&apos;s legitimate
                interests, consent, or another basis allowed by law. You may
                withdraw consent for future processing at any time where consent
                is the basis.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>5. How we disclose personal data</h2>
              <p>We disclose only what is reasonably needed to:</p>
              <ul>
                <li>
                  Payment, fraud-prevention, hosting, cloud, analytics,
                  communications, support, fulfillment, and delivery providers.
                </li>
                <li>
                  Professional advisers, insurers, auditors, and financial
                  institutions where needed for legitimate business or legal
                  purposes.
                </li>
                <li>
                  Authorities, courts, regulators, rights holders, or affected
                  parties when disclosure is required by law or reasonably
                  necessary to protect rights, safety, and security.
                </li>
                <li>
                  A buyer, investor, or successor in a proposed or completed
                  merger, financing, restructuring, or transfer of business,
                  subject to appropriate confidentiality and legal safeguards.
                </li>
                <li>
                  Another person when you direct us or give valid consent.
                </li>
              </ul>
              <p>
                Service providers are permitted to process data for defined
                services and must handle it under applicable privacy and
                security obligations.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>6. Advertising, sale, and sharing choices</h2>
              <p>
                We do not exchange personal data for money. Some privacy laws
                use &quot;sale&quot; or &quot;sharing&quot; more broadly for certain
                advertising or analytics disclosures. If we engage in activity
                covered by those definitions, we will provide any notice and
                opt-out method required by applicable law. We do not knowingly
                sell or share for targeted advertising the personal data of
                people we know are under the applicable minimum age.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>7. Cookies and similar technologies</h2>
              <p>
                We may use browser storage, pixels, tags, software development
                kits, and similar technologies for essential operation,
                preferences, measurement, and—where enabled—advertising. See our{" "}
                <Link to="/cookies">Cookie Policy</Link> for categories and
                controls.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>8. International data transfers</h2>
              <p>
                The providers used to operate an international store may process
                data in countries other than your own. Where required, we use an
                approved legal transfer mechanism, contractual protection, or
                another lawful safeguard. You may contact us for information
                about safeguards relevant to your data.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>9. Data retention</h2>
              <p>
                We keep personal data only as long as reasonably needed for the
                purpose collected, including fulfillment, returns, support,
                fraud prevention, and legal, tax, accounting, and dispute
                requirements. Retention varies by record type, legal duty,
                transaction status, and limitation period. Data may then be
                deleted, anonymized, or securely isolated until deletion is
                practical.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>10. Security</h2>
              <p>
                We use reasonable administrative, technical, and physical
                safeguards appropriate to the nature of the data and the risks
                involved. No online system or transmission can be guaranteed
                completely secure. You should protect account credentials,
                avoid sending payment details by ordinary email, and report
                suspicious activity promptly.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>11. Your choices and privacy rights</h2>
              <p>
                Depending on applicable law, you may have rights to access,
                correct, delete, restrict, or obtain a portable copy of personal
                data; object to or opt out of certain processing; withdraw
                consent; appeal a request decision; or complain to a regulator.
                You may also unsubscribe from marketing at any time.
              </p>
              <p>
                Email <a href="mailto:cost@averlynxstore.one">cost@averlynxstore.one</a>{" "}
                with the subject &quot;Privacy Request&quot;. We may verify the
                request using information already associated with you. We will
                not discriminate against you for exercising a protected right.
                Rights and exceptions differ by location; see our{" "}
                <Link to="/policies/regional-privacy">
                  Regional Privacy Rights &amp; Requests
                </Link>{" "}
                page.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>12. Children&apos;s privacy</h2>
              <p>
                The store is intended for a general audience and is not directed
                to children who cannot lawfully provide their own data or make a
                purchase. A parent or guardian who believes a child supplied
                personal data without appropriate permission should contact us
                so we can review and take appropriate action.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>13. Third-party links and services</h2>
              <p>
                Independent services have their own privacy practices. Review
                the notice presented by a payment wallet, social network,
                carrier, or other third party before giving it personal data.
                We are not responsible for an unrelated party&apos;s practices.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>14. Changes to this policy</h2>
              <p>
                We may update this policy to reflect changes in services,
                providers, or legal requirements. We will post the revised date
                and provide additional notice when required. A change does not
                reduce rights that already apply to previously collected data
                without a lawful basis.
              </p>
            </section>

            <section className="legal-page__section">
              <h2>15. Contact</h2>
              <p>
                For questions, requests, or complaints about privacy, email{" "}
                <a href="mailto:cost@averlynxstore.one">
                  cost@averlynxstore.one
                </a>
                . Please include enough information to understand the request,
                but do not email passwords, full payment-card numbers, or
                unnecessary identity documents.
              </p>
            </section>

            <aside className="legal-page__notice">
              This policy is designed to explain averlynxstore practices in
              clear language. Applicable privacy law controls if it gives you
              additional or non-waivable rights.
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}

export default Privacy;
